import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import intl from 'react-intl-universal'
import { FloatingBubble, Modal, CenterPopup, Popup, Skeleton } from 'antd-mobile'
import { AppstoreOutline, UnorderedListOutline } from 'antd-mobile-icons'
import { isInSellingTime } from '../../../utils/bookRule'
import ShoppingCart from './ShoppingCart'
import ProductDetail from '../../../components/ProductMenu/Detail'
import RelateAttrModal from '../../../components/ProductMenu/RelateAttrModal'
import math from '../../../utils/math'
import { formatJSON } from '../../../utils'
import { getProductLineAmount } from '../../../utils/price'

import RelateTagList from '../../../components/RelateTagList'
import AutoImage from '../../../components/AutoImage'
import NumStepper from '../../../components/NumStepper'
import ProductDesc from '../../../components/ProductDesc'
import PriceText from '../../../components/ProductMenu/PriceText'
import ConditionBar from './ConditionBar'
import ProductOriPrice from '../../../components/ProductOriPrice'
import PackageDiscountBadge from '../../../components/PackageDiscountBadge'

import PlusIcon from '../../../components/Icon/PlusIcon'
import { resetCart } from '../../../store/reducers/cart'
import { getShoppingCart, setShoppingCart } from '../../../utils/storage/store'
import { selectConCheckItems, resetConditionBar } from '../../../store/reducers/spa'

import defImg from '../../../assets/images/def_img_food.png'
import './index.less'

const VIEW_TYPES = {
    GRID: 1,
    LIST: 2
}

const SCROLL_THRESHOLD = 100

// 商品数量组件
const ProductNum = ({ items, productId, cls }) => {
    if (!items) return null

    const item = items?.filter(item => item.productId === productId)
    const quantity = item?.reduce((sum, item) => math.add(sum, item.quantity), 0)
    return quantity ? <div className={`product-num ${cls}`}>{quantity}</div> : null
}

const BookAdvanceDesc = ({ product }) => {
    const { extendColumn } = product
    const obj = formatJSON(extendColumn) || {}

    const { endAdvanceDay, endAdvanceHour,endAdvanceMinute, bookingTimeWithStore } = obj

    if (bookingTimeWithStore) return null

    if (endAdvanceDay || endAdvanceHour || endAdvanceMinute) {
        if (endAdvanceDay > 0 && endAdvanceHour > 0 && endAdvanceMinute > 0) {
            return <div className='product-desc'>
                {intl.get('book_advance_day_hour_minute', { day: endAdvanceDay, hour: endAdvanceHour, minute: endAdvanceMinute }).d(`提前${endAdvanceDay}天, ${endAdvanceHour}小时, ${endAdvanceMinute}分钟预订`)}
            </div>
        }
        if (endAdvanceDay > 0 && endAdvanceHour > 0) {
            return <div className='product-desc'>
                {intl.get('book_advance_day_hour', { day: endAdvanceDay, hour: endAdvanceHour }).d(`提前${endAdvanceDay}天, ${endAdvanceHour}小时预订`)}
            </div>
        }

        if (endAdvanceDay > 0) {
            return <div className='product-desc'>{intl.get('book_advance_day', { day: endAdvanceDay }).d(`提前${endAdvanceDay}天预订`)}</div>
        }

        if (endAdvanceHour > 0) {
            return <div className='product-desc'>{intl.get('book_advance_hour', { hour: endAdvanceHour }).d(`提前${endAdvanceHour}小时预订`)}</div>
        }
        if (endAdvanceMinute > 0) {
            return <div className='product-desc'>{intl.get('book_advance_minute', { minute: endAdvanceMinute }).d(`提前${endAdvanceMinute}分钟预订`)}</div>
        }
    }

    return null
}

const ProductMenu = ({ list, nextAction, readonly = false, merchantInfo, currentProduct, pageFlag, switchView = false, showTypeIcon = false, tagList = [], children, isFinished = false }) => {
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [cartItems, setCartItems] = useState([]);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewType, setViewType] = useState(VIEW_TYPES.GRID);
    const [attrVisible, setAttrVisible] = useState(false);
    const [detailVisible, setDetailVisible] = useState(false);

    const lastScrollYRef = useRef(window.scrollY);
    const categoryRefs = useRef([]); // 右侧每个分类的ref
    const scrollRef = useRef(null);  // 右侧滚动容器的ref
    const menuRefs = useRef([]); // 左侧菜单项的ref

    const dispatch = useDispatch()

    const conCheckItems = useSelector(selectConCheckItems)
    const { tagArr, typeArr } = conCheckItems

    const { storeId, extendColumn1, productImages } = merchantInfo || {}
    const extObj = formatJSON(extendColumn1) || {}
    const actShow = extObj.actShow || 0 //actShow 3 大图

    // 处理左侧分类点击
    const handleCategoryClick = (index) => {
        setSelectedCategory(index);
        // 点击左侧，右侧滚动到对应位置
        categoryRefs.current[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const scrollToElement = ({ target, offset = 0, gridHeight }) => {
        // 判断滚动方向

        let y = target.getBoundingClientRect().top + window.scrollY - offset;
        const isScrollUp = y < lastScrollYRef.current;

        if (y === lastScrollYRef.current) return

        if (isScrollUp) {
            y -= gridHeight;
        }

        const top = Math.max(0, y)

        window.scrollTo({ top, behavior: 'smooth' });
        lastScrollYRef.current = top; // 更新

    }

    const highlightTargetElement = (element) => {
        if (!element) return

        // 先移除其他高亮，避免页面上残留多个目标态
        document.querySelectorAll('.product-card-targeted').forEach(el => {
            el.classList.remove('product-card-targeted')
        })

        element.classList.remove('product-card-targeted')
        // 触发 reflow，确保重复点击同一项时动画也会重播
        void element.offsetWidth
        element.classList.add('product-card-targeted')

        setTimeout(() => {
            element.classList.remove('product-card-targeted')
        }, 1800)
    }

    // 处理右侧滚动
    const handleScroll = () => {
        if (!scrollRef.current) return;

        const container = scrollRef.current;
        const scrollTop = container.scrollTop;

        // 找到当前可视区域内的分类
        let currentCategory = 0;
        categoryRefs.current.forEach((ref, index) => {
            if (!ref) return;

            const rect = ref.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const offsetTop = rect.top - containerRect.top;

            if (offsetTop <= SCROLL_THRESHOLD) {
                currentCategory = index;
                // 让左侧菜单滚动到可视区域
                menuRefs.current[index]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        });

        setSelectedCategory(currentCategory);
    };

    const getProductPrice = (product) => getProductLineAmount(product, true)

    const updateCartItems = (newItems) => {
        setCartItems(newItems)
        setShoppingCart(newItems, storeId)
    }

    const addToCart = (item) => {
        setCartItems(prev => {
            const exists = prev.find(i =>
                i.productId === item.productId &&
                i.specsKey === item.specsKey &&
                i.attrKeys === item.attrKeys &&
                i.subAttrKeys === item.subAttrKeys
            )

            if (exists) {
                const newItems = prev.map(i => {
                    if (i.productId === item.productId &&
                        i.specsKey === item.specsKey &&
                        i.attrKeys === item.attrKeys &&
                        i.subAttrKeys === item.subAttrKeys) {
                        const newItem = { ...i, quantity: i.quantity + 1 }
                        return { ...newItem, amount: getProductPrice(newItem) }
                    }
                    return { ...i, amount: getProductPrice(i) }
                })
                updateCartItems(newItems)
                return newItems
            }

            const newItem = { ...item, quantity: 1 }
            const newItems = [...prev, { ...newItem, amount: getProductPrice(newItem) }]
            updateCartItems(newItems)
            return newItems
        })
    }

    const removeFromCart = (itemId, value, pd) => {
        setCartItems(prev => {
            const newItems = prev.reduce((acc, item) => {
                if (item.productId === pd.productId &&
                    item.specsKey === pd.specsKey &&
                    item.attrKeys === pd.attrKeys &&
                    item.subAttrKeys === pd.subAttrKeys) {
                    if (item.quantity > 1) {
                        const newItem = { ...item, quantity: item.quantity - 1 }
                        return [...acc, { ...newItem, amount: getProductPrice(newItem) }]
                    }
                    return acc
                }
                return [...acc, { ...item, amount: getProductPrice(item) }]
            }, [])

            updateCartItems(newItems)
            return newItems
        })
    }

    const showProductDetail = (item) => {
        Modal.show({
            content: <ProductDetail
                currentItem={item}
                addToCart={addToCart}
                cartList={cartItems}
                readonly={readonly}
            />,
            closeOnMaskClick: true
        })
    }

    const renderProductPrice = (item) => {
        if (item.priceType === 5) {
            return null
        }

        return <>
            <PriceText product={item} />
            <ProductOriPrice item={item} />
        </>
        // const { attrs = [], specs = [] } = item
        // const subAttrs = attrs.flatMap(attr => attr.subAttrList || [])
        // const minAttrPrice = subAttrs.length ? Math.min(...subAttrs.map(attr => attr.price)) : 0
        // const specPrice = specs.length ? Math.min(...specs.map(spec => spec.price)) : item.price
        // const amount = math.add(minAttrPrice, specPrice)
        // return `${amount} ${attrs.length > 0 ? '++' : ''}`
    }

    useEffect(() => {
        const items = getShoppingCart(storeId)
        if (items) {
            setCartItems(items)
        }
    }, [storeId])

    useEffect(() => {
        if (currentProduct && !pageFlag) {
            // 使用 flatMap 和 find 简化查找逻辑
            const targetProduct = list
                .flatMap(category => category.products)
                .find(product => product.productId === currentProduct.productId);

            if (targetProduct) {
                setCurrentItem(targetProduct);
                try {
                    const element = document.querySelector(`.js_pd_${currentProduct.productId}`);
                    if (element) {
                        element.scrollIntoView({
                            behavior: 'smooth', // 使用平滑滚动提升用户体验
                            // block: 'center' // 将元素滚动到视口中央
                        });
                    }
                } catch (error) {
                    console.error('滚动定位失败:', error);
                }
            }
        }
    }, [currentProduct, pageFlag, list]);

    useEffect(() => {
        if (actShow === 4) {
            setViewType(VIEW_TYPES.LIST)
        }
    }, [actShow])



    const viewCls = viewType === VIEW_TYPES.GRID ? '' : 'list-view'

    const handleClickImageAction = (item) => {
        setCurrentItem(item)
        setDetailVisible(true)
    }

    const renderProductCard = (item, isShow) => {
        if (!isShow) return null
        const inSellingTime = isInSellingTime(item.sellingTimeRange, item.sellingTimeRangeJson).state
        if (item.stock === 0 || item.status === 2 || !inSellingTime) return null

        const isGridView = viewType === VIEW_TYPES.GRID
        const productInfo = (
            <div className={`product-info ${isGridView ? 'jm-flex' : ''}`}>
                <div className='jm-flex-item' onClick={() => handleClickImageAction(item)}>
                    <h4 className='product-name'>
                        <span>{item.productName}</span>
                    </h4>
                    <BookAdvanceDesc product={item} />
                    {item.tagRelates && (
                        <div>
                            <RelateTagList list={item.tagRelates} />
                        </div>
                    )}
                    <div className='product-desc'>
                        <ProductDesc list={item.description} ellipsis={true} />
                    </div>
                    <div className="product-price">
                        <span>
                            {renderProductPrice(item)}
                        </span>
                    </div>
                </div>
                <div className='product-action'>
                    {!readonly && renderProductActions(item)}
                </div>
            </div>
        )

        const listProductInfo = (
            <div className='m-t-sm'>
                <div className={`product-info jm-flex`} style={{ gap: 10 }}>
                    <div className='jm-flex-item'>
                        <h4 className='product-name'
                            onClick={() => handleClickImageAction(item)}>
                            <span>{item.productName}</span>
                        </h4>
                        <BookAdvanceDesc product={item} />
                        {item.tagRelates && (
                            <div>
                                <RelateTagList list={item.tagRelates} />
                            </div>
                        )}


                    </div>
                    <div>
                        <div className="product-price">
                            <span>
                                {renderProductPrice(item)}
                            </span>
                        </div>
                    </div>
                    <div className='product-action'>
                        {!readonly && renderProductActions(item)}
                    </div>
                </div>
                <div className='product-desc'
                    onClick={() => handleClickImageAction(item)}
                    style={{
                        color: '#666',
                        fontSize: 12,
                        margin: '5px 15px 5px 0'
                    }}>
                    <ProductDesc list={item.description} ellipsis={true} />
                </div>
            </div>

        )

        return (
            <div key={item.productId} className={`product-card js_pd_${item.productId}`}>
                {isGridView && (
                    <div className='img-hold' onClick={() => handleClickImageAction(item)}>
                        <AutoImage
                            src={item.images || productImages || defImg}
                            alt={item.productName}
                            size='b'
                            onError={e => e.target.src = productImages || defImg}
                        />
                        <PackageDiscountBadge product={item} />
                    </div>
                )}
                {
                    isGridView
                        ?
                        productInfo :
                        listProductInfo
                }
            </div>
        )
    }

    // 渲染商品操作按钮
    const renderProductActions = (item, cls) => {
        if (item.priceType === 2 || item.priceType === 3) return null
        if (item.stock === 0) return null
        const subAttrLen = item.attrs.flatMap(attr => attr.subAttrList || []).length
        if ((item.attrs.length > 0 || subAttrLen > 0) || item.specs?.length > 0) {
            return (
                <div>
                    <button
                        style={{ background: 'transparent', border: 'none' }}
                        onClick={() => {
                            setCurrentItem(item)
                            setAttrVisible(true)
                        }}
                    >
                        <PlusIcon width={24} height={24} color="var(--adm-color-primary)" />
                        <ProductNum items={cartItems} productId={item.productId} cls={cls} />
                    </button>
                </div>
            )
        }

        return (
            <NumStepper
                items={cartItems}
                onAdd={item => {
                    if (item.attrs && item.attrs.length > 0) {
                        setCurrentItem(item)
                    } else {
                        addToCart(item)
                    }
                }}
                onRemove={removeFromCart}
                curProduct={item}
            />
        )
    }

    const doAddToCartOnDetail = (item) => {
        if (item.priceType === 2 || item.priceType === 3) return null
        if (item.stock === 0) return null
        const subAttrLen = item.attrs.flatMap(attr => attr.subAttrList || []).length
        if ((item.attrs.length > 0 || subAttrLen > 0) || item.specs?.length > 0) {
            // setCurrentItem(item)
            setAttrVisible(true)
        } else {
            addToCart(item)
        }
    }

    const renderCategorySection = (category, index) => {
        if (category.products.length === 0) return null

        let products = category.products
        if (tagArr.length > 0) {
            products = category.products.filter(product =>
                product.tagRelates?.filter(tag => tag !== null).some(tr =>
                    tagArr.some(tag => tag.relateTag === tr.relateTag)
                )
            )
            if (products.length === 0) return null
        }

        return (
            <div
                key={category.typeId}
                ref={el => categoryRefs.current[index] = el}
                className="category-section"
            >
                <h3 className={`category-title js_type_${category.typeId}`}>
                    {category.typeName}
                </h3>
                {viewType !== VIEW_TYPES.GRID && (
                    <div className={`category-img-wrapper js_img_${category.typeId}`}>
                        <AutoImage
                            src={category.icon || productImages}
                            alt={category.typeName}
                        />
                    </div>
                )}
                <div className={`product-grid js_grid_${category.typeId}`}>
                    {products.map(item => {
                        const isShow = tagArr.length === 0 ||
                            item.tagRelates?.some(tr =>
                                tagArr.some(tag => tag.relateTag === tr.relateTag)
                            )
                        return renderProductCard(item, isShow)
                    })}
                </div>
            </div>
        )
    }

    useEffect(() => {
        console.log(tagArr, '////====tagArr===')
        return () => {
            dispatch(resetConditionBar())
        }
    }, [])

    return (
        <>
            {children}
            <ConditionBar
                showSearch={true}
                products={list.flatMap(category => category.products)}
                tagList={tagList}
                typeList={list.map(item => ({
                    label: item.typeName,
                    value: item.typeId
                }))}
                callback={(obj) => {
                    const index = list.findIndex(item => item.typeId === obj.value)
                    if (index !== -1) {
                        const element = document.querySelector(`.js_type_${obj.value}`)
                        const grid = document.querySelector('.js_grid_' + obj.value)
                        const img = document.querySelector('.js_img_' + obj.value)
                        const gridHeight = grid ? grid.offsetHeight + (img ? img.offsetHeight : 0) : 0
                        if (element) {
                            setTimeout(() => {
                                scrollToElement({ target: element, offset: 60, gridHeight })
                            }, 500)
                        }
                    }
                }}
                productCallback={(product) => {
                    const targetId = product.productId || product.id
                    if (!targetId) return
                    const element = document.querySelector(`.js_pd_${targetId}`)
                    if (element) {
                        setTimeout(() => {
                            element.scrollIntoView({
                                // behavior: 'smooth',
                                block: 'center'
                            })
                            highlightTargetElement(element)
                        }, 500)
                    }
                }}
                tagCallback={(arr) => {
                    console.log(arr, '////====tag===', tagArr)
                }}
            />
            <div className="spa-menu-container">
                {
                    !isFinished && (
                        <div className='p10'>
                            <Skeleton.Title animated />
                            <Skeleton.Paragraph lineCount={3} animated />
                        </div>
                    )
                }
                <div className={`spa-menu-content ${viewCls}`}>
                    <div
                        className="product-list"
                        ref={scrollRef}
                        onScroll={handleScroll}
                    >
                        {list.map((category, index) => renderCategorySection(category, index))}
                        <div style={{ height: 40 }}>&nbsp;</div>
                    </div>

                    {!readonly && (
                        <ShoppingCart
                            items={cartItems}
                            onAdd={addToCart}
                            onRemove={removeFromCart}
                            clearCart={() => {
                                updateCartItems([])
                            }}
                            nextAction={nextAction}
                            storeInfo={merchantInfo}
                        />
                    )}
                </div>

                {/* 商品详情弹窗 */}
                <Popup
                    visible={detailVisible}
                    position='bottom'
                    showCloseButton
                    bodyStyle={{
                        backgroundColor: '#fff',
                        borderTopLeftRadius: '10px',
                        borderTopRightRadius: '10px',
                    }}
                    onClose={() => setDetailVisible(false)}
                    onMaskClick={() => setDetailVisible(false)}>
                    {
                        currentItem !== null && (
                            <ProductDetail
                                currentItem={currentItem}
                                addToCart={addToCart}
                                cartList={cartItems}
                                readonly={readonly}
                                removeFromCart={removeFromCart}
                                showAttrModal={doAddToCartOnDetail}
                                merchantInfo={merchantInfo}
                            >
                                {
                                    !readonly && (
                                        <ShoppingCart
                                            items={cartItems}
                                            onAdd={addToCart}
                                            onRemove={removeFromCart}
                                            nextAction={nextAction}
                                            storeInfo={merchantInfo}
                                            hideOrderBtn={true}
                                        />
                                    )
                                }

                            </ProductDetail>
                        )
                    }
                </Popup>

                {/* 属性选择弹窗 */}
                {currentItem !== null && attrVisible && (
                    <CenterPopup
                        className='product-detail-modal'
                        visible={attrVisible}
                        width={window.innerWidth}
                        closeOnMaskClick
                        onClose={() => setAttrVisible(false)}
                        onMaskClick={() => setAttrVisible(false)}
                        bodyStyle={{
                            backgroundColor: 'transparent',
                            padding: 0
                        }}
                        style={{
                            '--z-index': 1999
                        }}
                    >
                        <RelateAttrModal
                            product={currentItem}
                            addToCart={addToCart}
                            onCallback={() => {
                                // setCurrentItem(null)
                                setAttrVisible(false)
                                dispatch(resetCart())
                            }}
                        />
                    </CenterPopup>
                )}

                {switchView && (
                    <FloatingBubble
                        style={{
                            '--background': 'var(--adm-color-primary)',
                            '--initial-position-bottom': '100px',
                            '--initial-position-right': '10px',
                            '--edge-distance': '10px',
                        }}
                        onClick={() => {
                            setViewType(viewType === VIEW_TYPES.GRID ? VIEW_TYPES.LIST : VIEW_TYPES.GRID)
                        }}
                    >
                        {viewType === VIEW_TYPES.GRID ?
                            <UnorderedListOutline fontSize={30} /> :
                            <AppstoreOutline fontSize={30} />
                        }
                    </FloatingBubble>
                )}
            </div>
        </>
    )
}

export default ProductMenu
